import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchEvents, 
  fetchAllEvents,
  fetchUpcomingEvents,
  createEvent, 
  updateEvent, 
  deleteEvent,
  exportEvents,
  importEvents,
  EventsResponse,
  UpcomingEventsCountResponse
} from '@/api/events';
import { Event } from '@/types/revenue-manager';

export function useEvents(propertyId: string) {
  return useQuery<EventsResponse>({
    queryKey: ['events', propertyId],
    queryFn: () => fetchEvents(propertyId),
    enabled: !!propertyId,
  });
}

export function useAllEvents() {
  return useQuery({
    queryKey: ['events', 'all'],
    queryFn: fetchAllEvents,
  });
}

export function useUpcomingEventsCount() {
  return useQuery<UpcomingEventsCountResponse>({
    queryKey: ['events', 'upcoming-count'],
    queryFn: fetchUpcomingEvents,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (event: Omit<Event, 'id'>) => createEvent(event),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ eventId, updates }: { eventId: string; updates: Partial<Event> }) => 
      updateEvent(eventId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (eventId: string) => deleteEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useExportEvents(propertyId: string) {
  return useQuery({
    queryKey: ['events', propertyId, 'export'],
    queryFn: () => exportEvents(propertyId),
    enabled: false, // Only fetch when manually triggered
  });
}

export function useImportEvents() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ targetPropertyIds, events }: { targetPropertyIds: string[]; events: Omit<Event, 'id'>[] }) => 
      importEvents(targetPropertyIds, events),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}
